/**
 * Global toast notification utility.
 * Usage (in any 'use client' component):
 *   import { toast } from '@/lib/toast';
 *   toast('Campaign launched!', 'success');
 *   toast('Connection failed', 'error');     // persists until dismissed
 *   toast('Processing...', 'info');
 *
 * Behaviour:
 *  - Errors PERSIST until dismissed (so a failure is never missed).
 *  - Any toast that CONTAINS A LINK / URL also persists, becomes selectable,
 *    and shows a "Copy" button — so the user can read, copy, or click the URL
 *    without it disappearing the moment they touch it.
 *  - Persistent toasts only close via the ✕ button (clicking the body, links,
 *    or selecting text will NOT dismiss them).
 *  - Plain success/info toasts auto-fade after 4s.
 *  - Pass an explicit duration to override (0 = never auto-fade).
 */

function escapeHtml(s) {
    return String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
}

// Turn raw URLs in an already-escaped string into clickable links.
function linkify(escaped) {
    return escaped.replace(/(https?:\/\/[^\s<]+[^\s<.,)\]]) ?/g, (m, url) => {
        return `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color:var(--blue,#1d4ed8);font-weight:700;text-decoration:underline;word-break:break-all;">${url}</a> `;
    });
}

function extractUrls(message) {
    const m = String(message).match(/https?:\/\/[^\s<]+[^\s<.,)\]]/g);
    return m || [];
}

export function toast(message, type = 'info', duration) {
    if (typeof document === 'undefined') return;
    const container = document.getElementById('global-toast');
    if (!container) return;

    const isError = type === 'error';
    const urls = extractUrls(message);
    const hasLink = urls.length > 0;
    // Errors and any link-bearing message stay until dismissed; others auto-fade.
    const persistent = isError || hasLink;
    if (duration == null) duration = persistent ? 0 : 4000;

    const el = document.createElement('div');
    el.className = `toast-item toast-${type}`;
    // No emoji: the coloured rail on the left already carries the meaning.
    const icons = { success: '', error: '', info: '', warning: '' };

    const remove = () => {
        el.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
        el.style.opacity = '0';
        el.style.transform = 'translateY(10px)';
        setTimeout(() => el.remove(), 320);
    };

    if (persistent) {
        // Override any CSS auto-fade so it doesn't disappear on its own.
        el.classList.add('toast-persistent');
        el.style.animation = 'toastIn 0.5s cubic-bezier(0.22, 1.2, 0.36, 1)';
        // Let the user select / copy the text and click links inside.
        el.style.userSelect = 'text';
        el.style.cursor = 'default';
        el.style.maxWidth = '420px';
        el.style.display = 'flex';
        el.style.flexDirection = 'column';
        el.style.gap = '8px';

        const body = document.createElement('div');
        body.style.cssText = 'display:flex;align-items:flex-start;gap:8px;line-height:1.45;';
        body.innerHTML = `<span style="flex:1;">${linkify(escapeHtml(message))}</span>`;

        const actions = document.createElement('div');
        actions.style.cssText = 'display:flex;gap:8px;align-self:flex-end;';

        // Copy button — copies the URL if there is one, otherwise the whole message.
        const copyBtn = document.createElement('button');
        copyBtn.textContent = hasLink ? 'Copy link' : 'Copy';
        copyBtn.style.cssText = 'font-size:12.5px;font-weight:700;padding:5px 12px;border-radius:999px;cursor:pointer;';
        copyBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            const text = hasLink ? urls.join('\n') : String(message);
            const done = () => { const o = copyBtn.textContent; copyBtn.textContent = '✓ Copied'; setTimeout(() => { copyBtn.textContent = o; }, 1500); };
            try {
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    navigator.clipboard.writeText(text).then(done).catch(() => fallbackCopy(text, done));
                } else { fallbackCopy(text, done); }
            } catch (_) { fallbackCopy(text, done); }
        });

        // Dismiss button — the ONLY thing that closes a persistent toast.
        const closeBtn = document.createElement('button');
        closeBtn.textContent = '✕';
        closeBtn.title = 'Dismiss';
        closeBtn.style.cssText = 'font-size:12.5px;font-weight:800;padding:5px 12px;border-radius:999px;cursor:pointer;';
        closeBtn.addEventListener('click', (e) => { e.stopPropagation(); remove(); });

        actions.appendChild(copyBtn);
        actions.appendChild(closeBtn);
        el.appendChild(body);
        el.appendChild(actions);
    } else {
        el.textContent = String(message);
    }

    container.appendChild(el);

    if (duration > 0) {
        setTimeout(remove, duration);
    }
}

function fallbackCopy(text, done) {
    try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.cssText = 'position:fixed;top:-1000px;left:-1000px;opacity:0;';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        ta.remove();
        if (done) done();
    } catch (_) { /* no-op */ }
}

// Convenience methods so both call styles work:
//   toast('Saved', 'success')   AND   toast.success('Saved')
toast.success = (message, duration) => toast(message, 'success', duration);
toast.error = (message, duration) => toast(message, 'error', duration);
toast.info = (message, duration) => toast(message, 'info', duration);
toast.warning = (message, duration) => toast(message, 'warning', duration);
